package com.essntl.features.proposal.ui.proposalhistory

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.collectSuccessEvent
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.proposal.domain.repository.ProposalRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ProposalOverviewViewModel(
    private val proposalRepository: ProposalRepository,
    private val areaRepository: AreaRepository,
    private val categoryRepository: CategoryRepository,
    private val fileRepository: FileRepository,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<ProposalOverviewState>,
) : ViewModel {

    init {
        getAllArea()
        getAllCategory()

        getCurrentProfile()
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )
    }

    fun onEvent(event: ProposalOverviewEvent) {
        when (event) {
            is ProposalOverviewEvent.GetProposal -> getProposal(event)
            is ProposalOverviewEvent.GetProposalHistory -> getProposalHistory(event)
            is ProposalOverviewEvent.ClearState -> clearState()
            is ProposalOverviewEvent.ShowProposalOverview -> showProposalOverview(event)
        }
    }

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    private var getProposalJob: Job? = null
    private fun getProposalHistory(event: ProposalOverviewEvent.GetProposalHistory) {
        if (state.value.getProposal.data?.id == event.id) return

        getAllProposalService(event.id)

        getProposalJob?.cancel()
        getProposalJob = getData(
            getData = { proposalRepository.getProposalHistoryById(event.id) },
            getState = { state.value.getProposal },
            setState = {
                state.value = state.value.copy(
                    getProposal = it,
                    proposalParentStatus = event.proposalParentStatus,
                )
            },
            ignoreLoading = true,
        )
    }

    private fun showProposalOverview(event: ProposalOverviewEvent.ShowProposalOverview) {
        getAllProposalService(event.proposal.id)

        state.value = state.value.copy(
            getProposal = State.success(event.proposal),
        )
    }

    private fun getProposal(event: ProposalOverviewEvent.GetProposal) {
        if (state.value.getProposal.data?.id == event.id) return

        getAllProposalService(event.id)

        getProposalJob?.cancel()
        getProposalJob = getData(
            getData = { proposalRepository.getProposal(event.id) },
            getState = { state.value.getProposal },
            setState = {
                state.value = state.value.copy(
                    getProposal = it,
                    proposalParentStatus = it.data?.status,
                )
            },
            ignoreLoading = true,
            onSuccess = { p ->
                p.coverImage?.id?.let {
                    getCoverImage(it)
                }
            },
        )
    }

    private fun getAllArea() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { state.value = state.value.copy(getAllArea = it) },
        )

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { state.value = state.value.copy(getAllCategory = it) },
        )

    private fun getCoverImage(id: String) =
        getData(
            getData = { fileRepository.getById(id) },
            getState = { state.value.getCoverImage },
            setState = { newState -> state.update { it.copy(getCoverImage = newState) } },
        )

    private var getAllProposalServiceJob: Job? = null
    private fun getAllProposalService(proposalId: String) {
        getAllProposalServiceJob?.cancel()
        getAllProposalServiceJob = getData(
            getData = { proposalRepository.getAllProposalService(proposalId) },
            getState = { state.value.getAllProposalService },
            setState = { state.value = state.value.copy(getAllProposalService = it) },
        )
    }

    private fun clearState() {
        state.value = ProposalOverviewState(
            getAllRequest = state.value.getAllRequest,
            getAllArea = state.value.getAllArea,
            getCurrentProfile = state.value.getCurrentProfile,
        )
        getProposalJob?.cancel()
        getAllProposalServiceJob?.cancel()
    }
}